import { BookOpen, Sparkles, Calendar, ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { EDUCATION_TOPICS, IMAGES } from "@/lib/constants";
import { getSortedPosts } from "@/data/blogPosts";

function formatDate(iso) {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export default function Education() {
  const latestPosts = getSortedPosts().slice(0, 3);
  return (
    <div data-testid="education-page">
      <section className="py-14 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-12 gap-4 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7 animate-fade-up">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Education Hub
              </p>
              <h1 className="mt-4 font-display text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white">
                Understand your{" "}
                <span className="text-[#28C0D8]">wellness.</span>
              </h1>
              <p className="mt-6 text-[17px] text-[#94A3B8] max-w-2xl">
                Clear, non-judgmental guides to cannabis — from first-time
                questions to advanced terpene profiles. Built for Maryland
                patients and adult-use customers.
              </p>
            </div>
            <div className="col-span-12 md:col-span-5 hidden md:block">
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border border-white/10">
                <img
                  src={IMAGES.macro1}
                  alt="Cannabis close up"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EDUCATION_TOPICS.map((t, i) => (
              <article
                key={t.title}
                className="group rounded-2xl border border-white/10 bg-[#13161C] p-7 hover:border-[#28C0D8]/50 hover:-translate-y-1 transition-all duration-300"
                data-testid={`education-topic-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="h-10 w-10 rounded-xl bg-[#28C0D8]/10 text-[#28C0D8] flex items-center justify-center group-hover:bg-[#28C0D8] group-hover:text-[#0A0B0E] transition">
                    <BookOpen className="h-5 w-5" />
                  </div>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#28C0D8]">
                    {t.tag}
                  </span>
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-white tracking-tight">
                  {t.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[#94A3B8]">
                  {t.summary}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#0A0B0E]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold inline-flex items-center gap-2">
                <BookOpen className="h-3.5 w-3.5" /> From the Journal
              </p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">
                Latest cannabis articles.
              </h2>
              <p className="mt-3 text-[15px] text-[#94A3B8]">
                Deeper reads on Maryland cannabis — delivery rules, terpenes,
                dosing, COAs, and how to shop confidently. Written by our
                Bethesda team.
              </p>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white hover:bg-white/5"
              data-testid="education-view-all-blog"
            >
              All articles <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {latestPosts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`education-blog-${p.slug}`}
                className="group flex flex-col rounded-2xl border border-white/10 bg-[#13161C] hover:border-[#28C0D8]/40 transition-all overflow-hidden min-w-0"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-5 md:p-6 flex flex-col flex-1 min-w-0">
                  <div className="flex items-center gap-3 text-[11px] text-white/50">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" />
                      {formatDate(p.date)}
                    </span>
                    <span className="w-1 h-1 rounded-full bg-white/30" />
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3 w-3" />
                      {p.readingTime}
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-lg md:text-xl font-semibold tracking-tight text-white group-hover:text-[#28C0D8] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#94A3B8] line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-auto pt-5 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider text-white/60">
                      {(p.tags && p.tags[0]) || "Education"}
                    </span>
                    <ArrowRight className="h-4 w-4 text-[#28C0D8] opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#0A0B0E]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-14 py-20 md:py-24">
          <div className="rounded-3xl border border-white/10 bg-[#13161C] p-8 md:p-12">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-[#28C0D8]" />
              <p className="text-xs uppercase tracking-[0.22em] text-[#28C0D8] font-semibold">
                Responsible Use
              </p>
            </div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">
              Start low. Go slow.
            </h3>
            <p className="mt-4 max-w-2xl text-[15px] text-[#CBD5E1] leading-relaxed">
              Cannabis affects everyone differently. Our team will always
              recommend starting at the lowest effective dose, giving time for
              onset, and adjusting as you learn how your body responds. If
              you&apos;re ever uncertain, ask us — education is always part of
              the visit.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
